import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const CookiePolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <h1 className="text-3xl font-bold mb-6">Cookie Policy</h1>
        
        <div className="space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">Last Updated: {new Date().toLocaleDateString()}</p>
          
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. What Are Cookies</h2>
            <p>
              Cookies are small text files stored on your device when you visit our website. 
              They help us provide a better user experience by remembering your preferences 
              and authentication status.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. How We Use Cookies</h2>
            <p className="mb-2">Vinculum uses cookies for the following purposes:</p>
            
            <h3 className="text-xl font-semibold mb-2 mt-4">2.1 Essential Cookies (Required)</h3>
            <p>These cookies are necessary for the Service to function:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Authentication:</strong> Session cookies to keep you logged in</li>
              <li><strong>Security:</strong> CSRF protection tokens</li>
              <li><strong>Preferences:</strong> User interface settings</li>
            </ul>
            <p className="mt-2 text-sm italic">These cookies cannot be disabled as they are essential for the Service.</p>

            <h3 className="text-xl font-semibold mb-2 mt-4">2.2 Analytics Cookies (Optional)</h3>
            <p>We plan to use Google Analytics to understand how users interact with our Service:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Page views and navigation patterns</li>
              <li>Feature usage statistics</li>
              <li>Performance monitoring</li>
              <li>Error tracking</li>
            </ul>
            <p className="mt-2 text-sm italic">You can opt-out of analytics cookies through the cookie consent banner.</p>

            <h3 className="text-xl font-semibold mb-2 mt-4">2.3 Functional Cookies (Optional)</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Remember your language preference</li>
              <li>Save your dashboard customizations</li>
              <li>Store notification preferences</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. Cookie Details</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 mt-4">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 border-b text-left">Cookie Name</th>
                    <th className="px-4 py-2 border-b text-left">Purpose</th>
                    <th className="px-4 py-2 border-b text-left">Duration</th>
                    <th className="px-4 py-2 border-b text-left">Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2 border-b">session_token</td>
                    <td className="px-4 py-2 border-b">Authentication</td>
                    <td className="px-4 py-2 border-b">30 minutes</td>
                    <td className="px-4 py-2 border-b">Essential</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">cookie_consent</td>
                    <td className="px-4 py-2 border-b">Store consent preferences</td>
                    <td className="px-4 py-2 border-b">1 year</td>
                    <td className="px-4 py-2 border-b">Essential</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">_ga</td>
                    <td className="px-4 py-2 border-b">Google Analytics</td>
                    <td className="px-4 py-2 border-b">2 years</td>
                    <td className="px-4 py-2 border-b">Analytics</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">_gid</td>
                    <td className="px-4 py-2 border-b">Google Analytics</td>
                    <td className="px-4 py-2 border-b">24 hours</td>
                    <td className="px-4 py-2 border-b">Analytics</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Third-Party Cookies</h2>
            <p>We may use third-party services that set their own cookies:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Google Analytics:</strong> For usage analytics and insights</li>
              <li>These services have their own privacy policies</li>
              <li>We do not control third-party cookies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Managing Your Cookie Preferences</h2>
            <p className="mb-2">You have several options to manage cookies:</p>
            
            <h3 className="text-xl font-semibold mb-2 mt-4">5.1 Cookie Consent Banner</h3>
            <p>
              When you first visit our site, you'll see a cookie consent banner where you can 
              accept or reject optional cookies. You can change your preferences at any time.
            </p>

            <h3 className="text-xl font-semibold mb-2 mt-4">5.2 Browser Settings</h3>
            <p>Most browsers allow you to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>View what cookies are stored</li>
              <li>Delete specific or all cookies</li>
              <li>Block cookies from specific sites</li>
              <li>Block all third-party cookies</li>
              <li>Clear all cookies when you close your browser</li>
            </ul>
            <p className="mt-2 text-sm italic">
              Note: Blocking essential cookies may prevent you from using the Service.
            </p>

            <h3 className="text-xl font-semibold mb-2 mt-4">5.3 Opt-Out Tools</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Google Analytics Opt-out: <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Browser Add-on</a></li>
              <li>Do Not Track (DNT): We respect browser DNT signals</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. GDPR and Your Rights</h2>
            <p>
              Under GDPR, you have rights regarding cookies and tracking:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Right to withdraw consent at any time</li>
              <li>Right to access information collected through cookies</li>
              <li>Right to object to cookie-based processing</li>
              <li>Right to data portability</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy to reflect changes in our practices or for legal reasons. 
              We will notify you of significant changes through the Service or by email.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Contact Us</h2>
            <div className="mt-2 p-4 bg-gray-50 rounded-lg">
              <p><strong>Vinculum</strong></p>
              <p>Data Protection Officer: Kshitij Shah</p>
              <p className="mt-2">For questions about cookies, please contact us or manage your preferences through the cookie consent banner.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;